import { useMemo, useState } from 'react';
import type { Category, Product } from '@harbor/shared';
import { ApiError, apiRequest } from '../../lib/api.js';
import { useApi, useDebounced } from '../../lib/hooks.js';
import { money } from '../../lib/format.js';
import { useAuth } from '../../state/auth.js';
import {
  Alert,
  Badge,
  Button,
  Card,
  Field,
  Select,
  TextArea,
  TextInput,
} from '../../components/ui.js';

interface ProductFormState {
  id: string | null;
  name: string;
  slug: string;
  summary: string;
  description: string;
  price: string;
  compareAt: string;
  sku: string;
  stock: string;
  status: 'draft' | 'active' | 'archived';
  categoryId: string;
  tags: string;
}

const BLANK: ProductFormState = {
  id: null,
  name: '',
  slug: '',
  summary: '',
  description: '',
  price: '',
  compareAt: '',
  sku: '',
  stock: '0',
  status: 'draft',
  categoryId: '',
  tags: '',
};

/**
 * Product management.
 *
 * ## Stock is not an editable field
 *
 * The edit form deliberately omits it. Stock only moves through the dedicated
 * adjust endpoint, which requires a *reason* and writes an inventory-ledger row.
 * An editable stock box is how a system ends up unable to explain a discrepancy:
 * someone types 12, the number was 8, and there is no record of why.
 *
 * The API enforces this too — `PATCH /products/:id` ignores a submitted `stock`
 * value and writes an audit note saying so. This form just does not offer the
 * mistake in the first place.
 */
export function AdminProducts() {
  const { user } = useAuth();
  const [statusFilter, setStatusFilter] = useState('all');
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounced(search, 300);
  const [form, setForm] = useState<ProductFormState | null>(null);
  const [stockEditor, setStockEditor] = useState<Product | null>(null);
  const [error, setError] = useState<string | null>(null);

  const query = useMemo(() => {
    const params = new URLSearchParams();
    if (statusFilter !== 'all') params.set('status', statusFilter);
    if (debouncedSearch) params.set('q', debouncedSearch);
    const value = params.toString();
    return `/api/admin/products${value ? `?${value}` : ''}`;
  }, [statusFilter, debouncedSearch]);

  const { data, loading, refetch } = useApi<{ products: Product[] }>(query);
  const categories = useApi<{ categories: Category[] }>('/api/admin/categories');

  async function save() {
    if (!form) return;
    setError(null);

    const payload = {
      name: form.name.trim(),
      slug: form.slug.trim(),
      summary: form.summary.trim(),
      description: form.description.trim(),
      priceMinor: Math.round(Number(form.price) * 100),
      compareAtMinor: form.compareAt.trim() ? Math.round(Number(form.compareAt) * 100) : null,
      currency: 'USD' as const,
      sku: form.sku.trim(),
      stock: Number(form.stock),
      status: form.status,
      categoryId: form.categoryId || null,
      tags: form.tags
        .split(',')
        .map((tag) => tag.trim())
        .filter(Boolean),
    };

    try {
      if (form.id) {
        await apiRequest(`/api/admin/products/${form.id}`, { method: 'PATCH', body: payload });
      } else {
        await apiRequest('/api/admin/products', { method: 'POST', body: payload });
      }
      setForm(null);
      refetch();
    } catch (caught) {
      if (caught instanceof ApiError) {
        setError(
          caught.problem.errors?.map((issue) => `${issue.path}: ${issue.message}`).join(' · ') ??
            caught.problem.detail,
        );
      } else {
        setError('Could not save this product');
      }
    }
  }

  function edit(product: Product) {
    setForm({
      id: product.id,
      name: product.name,
      slug: product.slug,
      summary: product.summary,
      description: product.description,
      price: (product.priceMinor / 100).toFixed(2),
      compareAt: product.compareAtMinor !== null ? (product.compareAtMinor / 100).toFixed(2) : '',
      sku: product.sku,
      stock: String(product.stock),
      status: product.status,
      categoryId: product.category?.id ?? '',
      tags: product.tags.join(', '),
    });
    setError(null);
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-xl font-semibold tracking-tight">Products</h1>
          <p className="mt-1 text-sm text-muted">
            Draft products are invisible to the storefront. Archived products stay in order history.
          </p>
        </div>
        <Button onClick={() => { setForm({ ...BLANK }); setError(null); }}>New product</Button>
      </div>

      <div className="flex flex-wrap gap-3">
        <div className="w-full max-w-xs">
          <TextInput
            type="search"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Search by name or SKU"
            aria-label="Search products"
          />
        </div>
        <div className="w-40">
          <Select
            value={statusFilter}
            onChange={(event) => setStatusFilter(event.target.value)}
            aria-label="Filter by status"
          >
            <option value="all">All statuses</option>
            <option value="active">Active</option>
            <option value="draft">Draft</option>
            <option value="archived">Archived</option>
          </Select>
        </div>
      </div>

      {error && !form && (
        <Alert tone="danger" title="Something went wrong" onDismiss={() => setError(null)}>
          {error}
        </Alert>
      )}

      {form && (
        <Card className="p-5">
          <h2 className="text-sm font-semibold">{form.id ? 'Edit product' : 'New product'}</h2>

          {error && (
            <div className="mt-4">
              <Alert tone="danger" title="Could not save">
                {error}
              </Alert>
            </div>
          )}

          <div className="mt-5 grid gap-4 sm:grid-cols-2">
            <Field label="Name" htmlFor="p-name">
              <TextInput
                id="p-name"
                value={form.name}
                onChange={(event) => setForm({ ...form, name: event.target.value })}
              />
            </Field>
            <Field label="Slug" htmlFor="p-slug" hint="Lowercase words separated by hyphens.">
              <TextInput
                id="p-slug"
                value={form.slug}
                onChange={(event) => setForm({ ...form, slug: event.target.value })}
              />
            </Field>
            <Field label="SKU" htmlFor="p-sku">
              <TextInput
                id="p-sku"
                value={form.sku}
                onChange={(event) => setForm({ ...form, sku: event.target.value })}
              />
            </Field>
            <Field label="Category" htmlFor="p-category">
              <Select
                id="p-category"
                value={form.categoryId}
                onChange={(event) => setForm({ ...form, categoryId: event.target.value })}
              >
                <option value="">Uncategorised</option>
                {categories.data?.categories.map((category) => (
                  <option key={category.id} value={category.id}>
                    {category.name}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label="Price (USD)" htmlFor="p-price">
              <TextInput
                id="p-price"
                type="number"
                min="0"
                step="0.01"
                value={form.price}
                onChange={(event) => setForm({ ...form, price: event.target.value })}
              />
            </Field>
            <Field
              label="Compare-at price"
              htmlFor="p-compare"
              hint="Optional. Shown struck through when higher than the price."
            >
              <TextInput
                id="p-compare"
                type="number"
                min="0"
                step="0.01"
                value={form.compareAt}
                onChange={(event) => setForm({ ...form, compareAt: event.target.value })}
              />
            </Field>
            <Field label="Status" htmlFor="p-status">
              <Select
                id="p-status"
                value={form.status}
                onChange={(event) =>
                  setForm({ ...form, status: event.target.value as ProductFormState['status'] })
                }
              >
                <option value="draft">Draft</option>
                <option value="active">Active</option>
                <option value="archived">Archived</option>
              </Select>
            </Field>
            {!form.id && (
              <Field
                label="Initial stock"
                htmlFor="p-stock"
                hint="Recorded in the inventory ledger as opening stock."
              >
                <TextInput
                  id="p-stock"
                  type="number"
                  min="0"
                  value={form.stock}
                  onChange={(event) => setForm({ ...form, stock: event.target.value })}
                />
              </Field>
            )}
          </div>

          <div className="mt-4 space-y-4">
            <Field label="Summary" htmlFor="p-summary" hint="One sentence. Shown on the product card.">
              <TextInput
                id="p-summary"
                value={form.summary}
                onChange={(event) => setForm({ ...form, summary: event.target.value })}
              />
            </Field>
            <Field label="Description" htmlFor="p-description">
              <TextArea
                id="p-description"
                rows={5}
                value={form.description}
                onChange={(event) => setForm({ ...form, description: event.target.value })}
              />
            </Field>
            <Field label="Tags" htmlFor="p-tags" hint="Comma separated.">
              <TextInput
                id="p-tags"
                value={form.tags}
                onChange={(event) => setForm({ ...form, tags: event.target.value })}
              />
            </Field>
          </div>

          <div className="mt-5 flex justify-end gap-2">
            <Button variant="ghost" onClick={() => setForm(null)}>
              Cancel
            </Button>
            <Button onClick={() => void save()}>Save product</Button>
          </div>
        </Card>
      )}

      {stockEditor && (
        <StockEditor
          product={stockEditor}
          onClose={() => setStockEditor(null)}
          onSaved={() => {
            setStockEditor(null);
            refetch();
          }}
        />
      )}

      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[46rem] text-sm">
            <thead>
              <tr className="border-b border-line bg-raised text-left text-xs uppercase tracking-wide text-subtle">
                <th scope="col" className="px-4 py-3 font-medium">Product</th>
                <th scope="col" className="px-4 py-3 font-medium">SKU</th>
                <th scope="col" className="px-4 py-3 text-right font-medium">Price</th>
                <th scope="col" className="px-4 py-3 text-right font-medium">Stock</th>
                <th scope="col" className="px-4 py-3 font-medium">Status</th>
                <th scope="col" className="px-4 py-3 text-right font-medium">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-line">
              {loading && !data
                ? Array.from({ length: 5 }, (_, index) => (
                    <tr key={index}>
                      <td colSpan={6} className="px-4 py-3">
                        <div className="skeleton h-8 w-full rounded" />
                      </td>
                    </tr>
                  ))
                : data?.products.map((product) => (
                    <tr key={product.id} className="hover:bg-raised">
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          <div className="size-10 shrink-0 overflow-hidden rounded border border-line bg-sunken">
                            {product.images[0] ? (
                              <img src={product.images[0].url} alt="" className="size-full object-cover" />
                            ) : null}
                          </div>
                          <div className="min-w-0">
                            <p className="truncate font-medium">{product.name}</p>
                            <p className="truncate text-xs text-subtle">
                              {product.category?.name ?? 'Uncategorised'}
                            </p>
                          </div>
                        </div>
                      </td>
                      <td className="tabular px-4 py-3 text-xs text-muted">{product.sku}</td>
                      <td className="tabular px-4 py-3 text-right">
                        {money(product.priceMinor, product.currency)}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <button
                          type="button"
                          onClick={() => setStockEditor(product)}
                          className={[
                            'tabular rounded px-2 py-1 text-sm transition-colors hover:bg-sunken',
                            product.stock === 0
                              ? 'text-danger'
                              : product.stock <= 5
                                ? 'text-warning'
                                : 'text-ink',
                          ].join(' ')}
                        >
                          {product.stock}
                        </button>
                      </td>
                      <td className="px-4 py-3">
                        <Badge
                          tone={
                            product.status === 'active'
                              ? 'positive'
                              : product.status === 'draft'
                                ? 'warning'
                                : 'neutral'
                          }
                        >
                          {product.status}
                        </Badge>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex justify-end gap-1">
                          <Button variant="ghost" size="sm" onClick={() => edit(product)}>
                            Edit
                          </Button>
                          {user?.role === 'admin' && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={async () => {
                                setError(null);
                                try {
                                  const result = await apiRequest<{ archived?: boolean; message?: string }>(
                                    `/api/admin/products/${product.id}`,
                                    { method: 'DELETE' },
                                  );
                                  if (result?.message) setError(result.message);
                                  refetch();
                                } catch (caught) {
                                  setError(
                                    caught instanceof ApiError ? caught.problem.detail : 'Delete failed',
                                  );
                                }
                              }}
                            >
                              Delete
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
            </tbody>
          </table>
        </div>

        {data && data.products.length === 0 && (
          <p className="px-4 py-10 text-center text-sm text-muted">
            No products match those filters.
          </p>
        )}
      </Card>
    </div>
  );
}

/**
 * Stock adjustment.
 *
 * The reason field is required by the API and is surfaced here as a select
 * rather than free text, so the ledger stays queryable. The note is optional and
 * is where the human explanation goes.
 */
function StockEditor({
  product,
  onClose,
  onSaved,
}: {
  product: Product;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [delta, setDelta] = useState('');
  const [reason, setReason] = useState<'receive' | 'write_off' | 'correction' | 'return'>('receive');
  const [note, setNote] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const projected = product.stock + (Number(delta) || 0);

  async function submit() {
    setSaving(true);
    setError(null);
    try {
      await apiRequest(`/api/admin/products/${product.id}/stock`, {
        method: 'POST',
        body: { delta: Number(delta), reason, note },
      });
      onSaved();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.problem.detail : 'Could not adjust stock');
    } finally {
      setSaving(false);
    }
  }

  return (
    <Card className="p-5">
      <h2 className="text-sm font-semibold">Adjust stock — {product.name}</h2>
      <p className="mt-1 text-sm text-muted">
        Current stock {product.stock}. Every adjustment is written to the inventory ledger with the
        balance after the change.
      </p>

      {error && (
        <div className="mt-4">
          <Alert tone="danger" title="Could not adjust">
            {error}
          </Alert>
        </div>
      )}

      <div className="mt-5 grid gap-4 sm:grid-cols-3">
        <Field
          label="Change"
          htmlFor="s-delta"
          hint="Positive to receive, negative to write off."
        >
          <TextInput
            id="s-delta"
            type="number"
            placeholder="e.g. 12 or -3"
            value={delta}
            onChange={(event) => setDelta(event.target.value)}
          />
        </Field>
        <Field label="Reason" htmlFor="s-reason">
          <Select
            id="s-reason"
            value={reason}
            onChange={(event) => setReason(event.target.value as typeof reason)}
          >
            <option value="receive">Received from supplier</option>
            <option value="return">Customer return</option>
            <option value="write_off">Damaged or lost</option>
            <option value="correction">Stock count correction</option>
          </Select>
        </Field>
        <Field label="Note" htmlFor="s-note">
          <TextInput
            id="s-note"
            placeholder="Optional context"
            value={note}
            onChange={(event) => setNote(event.target.value)}
          />
        </Field>
      </div>

      {delta !== '' && Number.isFinite(Number(delta)) && (
        <p
          className={[
            'mt-4 text-sm',
            projected < 0 ? 'text-danger' : projected <= 5 ? 'text-warning' : 'text-muted',
          ].join(' ')}
        >
          {projected < 0
            ? `That would take stock to ${projected}, which is not possible.`
            : `Stock after this change: ${projected}`}
        </p>
      )}

      <div className="mt-5 flex justify-end gap-2">
        <Button variant="ghost" onClick={onClose}>
          Cancel
        </Button>
        <Button
          loading={saving}
          disabled={delta === '' || projected < 0}
          onClick={() => void submit()}
        >
          Apply adjustment
        </Button>
      </div>
    </Card>
  );
}
